import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { styled } from '@mui/material';

import { useI18n } from '@app-velocity/i18n';
import { useCurrentUser } from '@app-velocity/libs/auth';
import { useTransferTeamOptions } from '@app-velocity/libs/teams-data-access';
import {
  Button,
  ButtonsContainer,
  Modal,
  Select,
  Typography,
  useModalWrapperContext,
} from '@app-velocity/ui';

import { useMoveFigmaReportMutation } from '../../../../query/useMoveFigmaReportMutation';

const StyledContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

export const isMoveFigmaReportModalData = (
  data: unknown
): data is {
  id: string;
} => {
  const castedData = data as { id: string };

  return (
    'id' in castedData && !!castedData.id && typeof castedData.id === 'string'
  );
};

export const MoveFigmaReportModalContent = () => {
  const { t } = useI18n();
  const user = useCurrentUser();
  const { modalData, openedModalId, closeModal } = useModalWrapperContext();
  const { teamId, workspaceId } = useParams<{
    teamId: string;
    workspaceId: string;
  }>();
  const [selectedTeamId, setSelectedTeamId] = useState('');

  const { id } = useMemo(() => {
    const currentModalData = modalData[openedModalId];

    if (isMoveFigmaReportModalData(currentModalData)) {
      return currentModalData;
    }

    return { id: null };
  }, [modalData, openedModalId]);

  const { options, isLoading: areTeamOptionsLoading } = useTransferTeamOptions(
    user?.id,
    teamId
  );

  const selectedOption = options.find(
    (option) => option.teamId === selectedTeamId
  );

  const {
    mutate: moveReport,
    isPending,
    isSuccess,
  } = useMoveFigmaReportMutation(workspaceId, teamId);

  useEffect(() => {
    if (!isSuccess) return;

    closeModal();
  }, [isSuccess, closeModal]);

   
  useEffect(() => {
    setSelectedTeamId('');
  }, [openedModalId]);

  const handleTransfer = () => {
    if (!id || !selectedOption) return;

    moveReport({
      prototypeId: id,
      targetProjectId: selectedOption.projectId,
    });
  };

  const handleCancel = () => {
    closeModal();
  };

  return (
    <Modal>
      <Modal.Header title="prototypes:report.transfer.title" />
      <Modal.Content>
        <StyledContent>
          <Typography>{t('prototypes:report.transfer.text')}</Typography>
          <Select
            displayEmpty
            label={t('prototypes:report.transfer.team_label')}
            variant="outlined"
            value={selectedTeamId}
            disabled={areTeamOptionsLoading || isPending}
            onChange={(e) => setSelectedTeamId(e.target.value as string)}
            MenuProps={{
              disablePortal: true,
            }}
          >
            <Select.Item value="" disabled>
              {t('prototypes:report.transfer.team_placeholder')}
            </Select.Item>
            {options.map((option) => (
              <Select.Item key={option.teamId} value={option.teamId}>
                {option.teamName}
              </Select.Item>
            ))}
          </Select>
        </StyledContent>
      </Modal.Content>
      <Modal.Controls>
        <ButtonsContainer>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={handleCancel}
            disabled={isPending}
          >
            {t('shared:cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleTransfer}
            disabled={isPending || !id || !selectedOption}
          >
            {t('shared:transfer')}
          </Button>
        </ButtonsContainer>
      </Modal.Controls>
    </Modal>
  );
};
